import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, Eye, AlertCircle } from 'lucide-react';
import type { Note } from '../../types';

interface AccessFormProps {
  note: Note;
  password: string;
  isDecrypting: boolean;
  error: string | null;
  onPasswordChange: (password: string) => void;
  onSubmit: (e: React.FormEvent) => Promise<void>;
  isBlocked?: boolean;
}

export default function AccessForm({
  note,
  password,
  isDecrypting,
  error,
  onPasswordChange,
  onSubmit,
  isBlocked = false
}: AccessFormProps) {
  const { t } = useTranslation('viewNote');
  const [touched, setTouched] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onPasswordChange(e.target.value);
    setTouched(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isDecrypting || isBlocked) return;
    await onSubmit(e);
  };

  const showError = error && touched && !isDecrypting;

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {note.encrypted && (
        <div className="bg-white rounded-2xl shadow-sm border p-6 space-y-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              <Lock className="w-4 h-4 inline mr-2" />
              {t('form.password.label')}
            </label>
            <div>
              <input
                type="password"
                value={password}
                onChange={handleChange}
                className={`
                  w-full p-3 border rounded-xl 
                  focus:outline-none focus:ring-2 transition duration-200
                  ${showError 
                    ? 'border-red-300 focus:ring-red-100 focus:border-red-400' 
                    : 'border-gray-200 focus:ring-blue-100 focus:border-blue-400'
                  }
                `}
                placeholder={t('form.password.placeholder')}
                disabled={isDecrypting || isBlocked}
                aria-invalid={showError ? 'true' : 'false'}
                aria-describedby={showError ? 'password-error' : undefined}
              />
              {showError && (
                <div 
                  id="password-error"
                  className="flex items-center mt-2 text-sm text-red-600"
                  role="alert"
                >
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mr-2" />
                  <span>{error}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm border p-6">
        <button
          type="submit"
          disabled={isDecrypting || isBlocked}
          className={`
            w-full bg-blue-500 text-white py-4 rounded-xl
            hover:bg-blue-600 flex items-center justify-center
            transition-all duration-200 text-lg font-medium
            ${(isDecrypting || isBlocked) ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          {isDecrypting ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              {t('loading.decrypting')}
            </>
          ) : (
            <>
              <Eye className="w-5 h-5 mr-2" />
              {t('form.submit')}
            </>
          )}
        </button>
      </div>
    </form>
  );
}